/**
 * Hybridization (av-methodology-hybridization spec; design D13).
 *
 * ── HISTORICAL RECORD (2010, thesis Apêndice 4, p.137) — permanent, never executed ──
 *
 *   NPC = Σ(C · pr) / Σ pr      Sendo: NPC = Nova Posição Coluna, C = Número da coluna,
 *                                       pr = peso da relevância
 *
 *   NPL = Σ(L · pc) / Σ pc      Sendo: NPL = Nova Posição Linha, C = Número da coluna [sic —
 *                                       the thesis's own legend repeats "C" here instead of
 *                                       "L = Número da linha"; preserved verbatim, not corrected]
 *                                       pc = peso das características
 *
 * Applied literally to the thesis's own worked example (three circles resolving "catálogo de
 * produtos"), this does NOT reproduce the thesis's stated result (gives NPC=3.25→3, not 4). This
 * inconsistency is part of the permanent historical record — see `methodology.json`'s
 * `hybridization.historical` block and design.md D13 for the full mathematical trail. No function
 * in this module evaluates this formula; it is documented here for traceability only.
 *
 * ── OPERATIONAL FORMULA (Metodologia AV 2.0, 2026) — author-clarified, implemented below ──
 *
 *   NPC = Σ(C × pc) / Σ pc
 *   NPL = Σ(L × pr) / Σ pr
 *
 * The weight pairing is the confirmed resolution of OQ1 (design D13): the printed *legend* was
 * the part of the 2010 text in error, not the worked example. Non-integer results use ceiling
 * rounding (`Math.ceil`) — also an explicit 2026 author clarification (design D13), not thesis
 * content: "resultado inteiro permanece inalterado; resultado fracionário avança para o próximo
 * inteiro." `C`/`L` are 1-indexed (design D2): C matches `archetypes.json`'s `order` field
 * (Documento=1…Celular=12); L is 1=Baixo, 2=Médio, 3=Alto.
 *
 * This implementation is validated against the 2010 worked example (see
 * tests/domain/hybridization.test.ts): coluna 4, linha 2 — the thesis's own stated result.
 */

export const HYBRIDIZATION_REVIEW_STATUS = "requires-formula-validation" as const;

/** One related content node contributing to a hybridization calculation. 1-indexed per design D2. */
export interface HybridizationCircle {
  /** 1-indexed column number, matching archetypes.json's `order`. */
  coluna: number;
  /** peso da relevância (weight), used — per the confirmed 2026 pairing — to weight the LINE. */
  pr: number;
  /** 1-indexed line number: 1=Baixo, 2=Médio, 3=Alto. */
  linha: number;
  /** peso das características (weight), used — per the confirmed 2026 pairing — to weight the COLUMN. */
  pc: number;
}

export interface HybridizationResult {
  origin: "review-2026";
  /** 1-indexed resulting column (design D2). */
  coluna: number;
  /** 1-indexed resulting line (design D2). */
  linha: number;
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

/**
 * Compute Metodologia AV 2.0's operational hybridization result from a set of
 * related content nodes. Requires at least one circle. Uses the confirmed
 * 2026 weight pairing and ceiling rounding (design D13) — never the literal
 * 2010 formula, which remains an unexecuted historical record above.
 */
export function calculateHybridization(circles: HybridizationCircle[]): HybridizationResult {
  if (circles.length === 0) {
    throw new Error("calculateHybridization: at least one related content node (circle) is required.");
  }

  const sumPc = sum(circles.map((c) => c.pc));
  const sumPr = sum(circles.map((c) => c.pr));

  if (sumPc === 0 || sumPr === 0) {
    throw new Error("calculateHybridization: weights (pr/pc) must not sum to zero.");
  }

  const rawColuna = sum(circles.map((c) => c.coluna * c.pc)) / sumPc;
  const rawLinha = sum(circles.map((c) => c.linha * c.pr)) / sumPr;

  return {
    origin: "review-2026",
    coluna: Math.ceil(rawColuna),
    linha: Math.ceil(rawLinha),
  };
}

/**
 * Maps a ContentNode's InfluenceNodes to hybridization circles and computes
 * its resulting position (design D16). Each InfluenceNode carries a single
 * `weight`, applied as both `pr` and `pc` — a documented simplification
 * (design D16) relative to Apêndice 4's worked example, which uses two
 * independently-tunable numbers per circle; the UI does not yet expose both.
 *
 * Note: per Apêndice 4's worked example, the content's OWN current position
 * is not itself one of the averaged circles — only its independent
 * influences are. Requires at least one influence (calculateHybridization's
 * own precondition); callers should gate this on "at least one influence
 * exists" rather than calling it unconditionally.
 */
export function calculateContentHybridization(
  influences: Array<{ order: number; relevanceValue: number; weight: 1 | 2 | 3 }>,
): HybridizationResult {
  const circles: HybridizationCircle[] = influences.map((influence) => ({
    coluna: influence.order,
    pr: influence.weight,
    linha: influence.relevanceValue,
    pc: influence.weight,
  }));
  return calculateHybridization(circles);
}
